import React, { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InsightsIcon from '@mui/icons-material/Insights';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { apiUrl, fetchJson } from '../../apiBase';
import { getObservationSummary } from '../api/observationJournal';

const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return dateFormatter.format(date);
  } catch (_) {
    return date.toLocaleString('tr-TR');
  }
};

export default function ObservationSummaryPage({ schoolId, user, onError }) {
  const [classOptions, setClassOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadClasses = async () => {
      if (!schoolId) return;
      setLoadingClasses(true);
      try {
        const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`));
        if (!res.ok) throw new Error(await res.text());
        const rows = await res.json();
        const options = (rows || [])
          .map((item) => {
            if (!item) return null;
            if (typeof item === 'string') {
              return null;
            }
            const rawId = item?.id ?? item?.class_id;
            const id = Number(rawId);
            const name = item?.name ?? item?.code;
            if (!Number.isFinite(id) || !name) return null;
            return { id, name: String(name) };
          })
          .filter(Boolean)
          .sort((a, b) => a.name.localeCompare(b.name, 'tr-TR'));
        if (!cancelled) {
          setClassOptions(options);
          setSelectedClass((prev) => {
            if (prev && options.some((opt) => opt.id === prev.id)) {
              return prev;
            }
            if (!prev && options.length === 1) {
              return options[0];
            }
            return null;
          });
        }
      } catch (_) {
        if (!cancelled) onError?.('Şubeler yüklenemedi');
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    };
    loadClasses();
    return () => {
      cancelled = true;
    };
  }, [schoolId, onError]);

  useEffect(() => {
    let cancelled = false;
    const loadSummary = async () => {
      const classId = selectedClass?.id;
      if (!schoolId || !classId) {
        setSummary(null);
        return;
      }
      setLoadingSummary(true);
      try {
        const data = await getObservationSummary(schoolId, classId);
        if (!cancelled) {
          setSummary(data);
        }
      } catch (err) {
        if (!cancelled) {
          setSummary(null);
          onError?.(err?.message || 'Gözlem özeti alınamadı');
        }
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };
    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedClass, onError]);

  const students = useMemo(() => summary?.students || [], [summary]);
  const totalPending = useMemo(() => {
    if (!summary) return 0;
    return Math.max((summary.total_entries || 0) - (summary.total_sent_entries || 0), 0);
  }, [summary]);
  const isTeacher = user?.role === 'teacher';

  return (
    <Box sx={{ bgcolor: '#F9FAFB', minHeight: '100%', py: { xs: 2, md: 4 }, px: { xs: 2, md: 4 } }}>
      <Stack spacing={3}>
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <InsightsIcon color="primary" sx={{ fontSize: 36 }} />
              <Box>
                <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.4 }}>
                  Gözlem Özeti
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Şube bazında gözlem performansını izleyin
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Toplam gözlem sayısı ile WhatsApp üzerinden gönderilen kayıtları karşılaştırın.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
              <Autocomplete
                fullWidth
                options={classOptions}
                value={selectedClass}
                onChange={(_, value) => {
                  setSelectedClass(value || null);
                }}
                getOptionLabel={(option) => option?.name || ''}
                loading={loadingClasses}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Şube"
                    placeholder="Şube seçin"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingClasses ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {isTeacher && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Öğretmenler yalnızca haftalık programlarında yer alan şubeleri görüntüleyebilir.
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Toplam Öğrenci
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {summary ? summary.total_students : '—'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Toplam Gözlem
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {summary ? summary.total_entries : '—'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                WhatsApp Gönderildi
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                {summary ? summary.total_sent_entries : '—'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Bekleyen Gönderim
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                {summary ? totalPending : '—'}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
          {loadingSummary ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Özet yükleniyor…
              </Typography>
            </Box>
          ) : summary && students.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Öğrenci</TableCell>
                    <TableCell align="center">No</TableCell>
                    <TableCell align="center">Toplam</TableCell>
                    <TableCell align="center">Gönderilen</TableCell>
                    <TableCell align="center">Bekleyen</TableCell>
                    <TableCell align="center">Son Kayıt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((row) => {
                    const pending = Math.max((row.total_entries || 0) - (row.sent_entries || 0), 0);
                    return (
                      <TableRow key={row.student_id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AssessmentIcon fontSize="small" color="action" />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {row.student_name || String(row.student_id)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.class_name || summary.class_name}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">{row.student_no}</TableCell>
                        <TableCell align="center">{row.total_entries}</TableCell>
                        <TableCell align="center">
                          <Chip label={row.sent_entries} size="small" color="success" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={pending} size="small" color={pending ? 'warning' : 'default'} variant="outlined" />
                        </TableCell>
                        <TableCell align="center">{formatDate(row.last_entry_at)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : selectedClass ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Bu şube için gözlem kaydı bulunamadı.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Şube seçildiğinde öğrenci listesi burada görünecek.
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
